import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, User, DollarSign, Gauge, TrendingUp, CheckCircle2, ListTodo, AlertOctagon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import PageHeader from "@/components/PageHeader";
import { StatusBadge, RiskBadge, severityFromScore } from "@/components/StatusBadge";
import { useLang } from "@/context/LangContext";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";

const LAYERS = ["model", "system", "operation", "absorption", "outcome"];
const SEV_LABEL = { optimal: "Optimal", warning: "At Risk", critical: "Critical Gap" };
const SEV_LABEL_AR = { optimal: "مثالي", warning: "معرض للخطر", critical: "فجوة حرجة" };

export default function UseCaseDetail() {
  const { uid } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [uc, setUc] = useState(null);
  const [sim, setSim] = useState({});

  useEffect(() => {
    api.get(`/usecases/${uid}`).then(({ data }) => {
      setUc(data);
      const s = {}; LAYERS.forEach((k) => (s[k] = data.scores[k])); setSim(s);
    });
  }, [uid]);

  if (!uc) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  const simOverall = Math.round(LAYERS.reduce((a, k) => a + sim[k], 0) / LAYERS.length);
  const name = lang === "ar" ? uc.name_ar : uc.name;
  const outcomeLow = sim.outcome < 50 || uc.scores.outcome < 50;

  const getContext = () => ({ name: uc.name, domain: uc.domain, status: uc.lifecycleStatus, risk: uc.riskLevel,
    scores: uc.scores, overallReadiness: uc.overallReadiness, roi: uc.roi, adoption: uc.adoptionRate,
    absorptionScore: uc.scores.absorption, outcomeScore: uc.scores.outcome, businessValue: uc.businessValue,
    biggestGaps: LAYERS.filter((k) => uc.scores[k] < 50).map((k) => k) });

  return (
    <div className="space-y-6 fade-up" data-testid="usecase-detail-page">
      <button onClick={() => navigate("/use-cases")} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t("use_cases")}
      </button>

      <PageHeader title={name} subtitle={`${uc.uid} · ${lang === "ar" ? uc.domain_ar : uc.domain}`} screen={`use-case-${uc.uid}`} getContext={getContext}>
        <RiskBadge risk={uc.riskLevel} />
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={User} label={t("business_owner")} value={uc.businessOwner} />
        <Stat icon={Gauge} label={t("overall_readiness")} value={`${uc.overallReadiness}%`} />
        <Stat icon={TrendingUp} label={t("roi")} value={`${uc.roi}%`} />
        <Stat icon={DollarSign} label={t("value")} value={fmtMoney(uc.businessValue)} />
        <Stat icon={CheckCircle2} label={t("adoption_rate")} value={`${uc.adoptionRate}%`} />
      </div>

      {outcomeLow && (
        <Card className="p-4 rounded-2xl border-amber-500/40 bg-amber-500/10 flex items-start gap-3" data-testid="not-proven-banner">
          <AlertOctagon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{t("not_proven")}</p>
        </Card>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {LAYERS.map((k) => {
          const layer = uc.layers[k];
          const sev = severityFromScore(sim[k]);
          return (
            <Card key={k} className="p-5 rounded-2xl shadow-sm flex flex-col" data-testid={`layer-card-${k}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-bold uppercase tracking-wide">{t(k)}</h3>
                <StatusBadge severity={sev} label={lang === "ar" ? SEV_LABEL_AR[sev] : SEV_LABEL[sev]} />
              </div>
              <div className="flex items-end gap-2 mb-1">
                <span className="text-4xl font-bold">{sim[k]}</span>
                <span className="text-sm text-muted-foreground mb-1.5">/ 100</span>
              </div>
              <Slider value={[sim[k]]} onValueChange={([v]) => setSim((s) => ({ ...s, [k]: v }))} max={100} step={1}
                className="my-3" data-testid={`layer-slider-${k}`} />
              <div className="mt-2 space-y-3 flex-1">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{t("gaps")}</p>
                  {layer.gaps.length ? (
                    <ul className="space-y-1">
                      {layer.gaps.map((g, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-sm text-foreground/80">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />{g}
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-emerald-600 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" />{t("no_gaps")}</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1"><ListTodo className="h-3.5 w-3.5" />{t("actions")}</p>
                  <ul className="space-y-1">
                    {layer.actions.map((a, i) => <li key={i} className="text-sm text-foreground/80">• {a}</li>)}
                  </ul>
                </div>
              </div>
            </Card>
          );
        })}

        <Card className="p-5 rounded-2xl bg-primary text-primary-foreground shadow-md flex flex-col justify-center items-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/70">{t("overall_readiness")}</p>
          <p className="text-6xl font-bold my-2">{simOverall}<span className="text-2xl">%</span></p>
          <p className="text-sm text-primary-foreground/80 text-center">{lang === "ar" ? uc.description_ar : uc.description}</p>
        </Card>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-4 rounded-2xl shadow-sm">
      <div className="flex items-center gap-2 text-muted-foreground mb-1.5"><Icon className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider truncate">{label}</span></div>
      <p className="text-lg font-bold truncate">{value}</p>
    </Card>
  );
}
